import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const EditAuthorizationForm = ({ authorization, onCancel, onUpdated }) => {
  // Add safety check for auth object and provide default values
  const [formData, setFormData] = useState({
    authorization_id: "",
    consumer_id: "",
    service_id: "",
    plan_id: "",
    authorization_number: "",
    approved_by: "",
    approval_date: "",
    authorization_hours: "",
    authorization_rate_variance: "",
    auth_type: "service",
    complete: false,
    no_more_billing: false,
    user_id: "",
    created_by: "",
    updated_by: "",
    tenant_id: "",
    ...authorization,
    auth_type: authorization?.auth_type || "service",
  });

  const authWithoutPlan = !formData.plan_id;

  const [consumers, setConsumers] = useState([]);
  const [plans, setPlans] = useState([]);
  const [services, setServices] = useState([]);
  const [showPrivacyNotice, setShowPrivacyNotice] = useState(true);

  // Milestone state
  const [milestones, setMilestones] = useState([]);
  const [loadingMilestones, setLoadingMilestones] = useState(false);
  const [milestoneTemplates, setMilestoneTemplates] = useState([]);
  const [showAddMilestones, setShowAddMilestones] = useState(false);
  const [addMilestoneMode, setAddMilestoneMode] = useState("auto"); // "auto", "template", "custom"
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [newMilestones, setNewMilestones] = useState([]);
  const [savingMilestones, setSavingMilestones] = useState(false);
  const [editingMilestoneId, setEditingMilestoneId] = useState(null);
  const [editMilestoneData, setEditMilestoneData] = useState({ milestone_name: "", description: "" });

  // Get tenant_id and user info from useAuth
  const { tenant_id, user } = useAuth();
  const updated_by = user?.user_id || null;

  // Check if selected service is flat rate - use authorization prop directly
  // The authorization already has flat_rate from the backend query
  const isFlatRateService = authorization?.flat_rate === true;

  // Remove duplicates from consumers
  const uniqueConsumers = [
    ...new Map(consumers.map((c) => [c.consumer_id, c])).values(),
  ];

  // Load consumers
  useEffect(() => {
    if (!user || !tenant_id) return;

    const fetchConsumers = async () => {
      try {
        const res = await secureAxios.get("/api/consumers", {
          params: {
            tenant_id,
            user_id: user.user_id,
            role: user.role,
            tenant_type: user.tenant_type,
          },
        });
        setConsumers(res.data);
      } catch (err) {
        console.error("[X] Failed to load consumers", err);
        toast.error("Failed to load consumers");
      }
    };

    fetchConsumers();
  }, [tenant_id, user]);

  // Load available plans for the consumer
  useEffect(() => {
    if (!formData.consumer_id) return;

    const fetchPlans = async () => {
      try {
        const res = await secureAxios.get(
          `/api/plans/for-edit/${formData.consumer_id}/${
            formData.plan_id || "none"
          }`,
          { params: { tenant_id } }
        );
        setPlans(res.data);
      } catch (err) {
        console.error("[X] Failed to load plans", err);
        setPlans([]);
      }
    };

    fetchPlans();
  }, [formData.consumer_id, formData.plan_id, tenant_id]);

  // Load services for selected plan or from counselor's customer
  useEffect(() => {
    const fetchServices = async () => {
      try {
        if (formData.plan_id) {
          const res = await secureAxios.get(
            `/api/plans/${formData.plan_id}/services`,
            { params: { tenant_id } }
          );
          setServices(res.data);
        } else if (formData.consumer_id) {
          const selectedConsumer = consumers.find(
            (c) => c.consumer_id === formData.consumer_id
          );
          if (selectedConsumer?.counselor_id) {
            const counselorRes = await secureAxios.get(
              `/api/counselors/${selectedConsumer.counselor_id}`,
              { params: { tenant_id } }
            );
            const customer_id = counselorRes.data.customer_id;

            if (customer_id) {
              const servicesRes = await secureAxios.get("/api/services", {
                params: { tenant_id, customer_id },
              });
              setServices(servicesRes.data);
            }
          }
        }
      } catch (err) {
        console.error("[X] Failed to load services", err);
        setServices([]);
      }
    };

    fetchServices();
  }, [formData.plan_id, formData.consumer_id, consumers, tenant_id]);

  // Load milestones for flat rate authorization
  useEffect(() => {
    if (!formData.authorization_id || !tenant_id) return;

    const fetchMilestones = async () => {
      setLoadingMilestones(true);
      try {
        const res = await secureAxios.get(
          `/api/auth-milestones/auth/${formData.authorization_id}`,
          { params: { tenant_id } }
        );
        setMilestones(res.data || []);
      } catch (err) {
        console.error("Error loading milestones:", err);
        setMilestones([]);
      } finally {
        setLoadingMilestones(false);
      }
    };

    fetchMilestones();
  }, [formData.authorization_id, tenant_id]);

  // Load milestone templates
  useEffect(() => {
    if (!tenant_id || !isFlatRateService) return;

    secureAxios
      .get("/api/milestone-templates", {
        params: { tenant_id, include_items: "true" },
      })
      .then((res) => setMilestoneTemplates(res.data || []))
      .catch((err) => console.error("Error loading templates:", err));
  }, [tenant_id, isFlatRateService]);

  // Add early return if auth is not provided
  if (!authorization) {
    return (
      <div className="alert alert-danger">
        <strong>Error:</strong> No authorization data provided for editing.
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "consumer_id" ? { plan_id: "", service_id: "" } : {}),
      ...(name === "plan_id" ? { service_id: "" } : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const oldValues = {
        consumer_id: authorization.consumer_id,
        service_id: authorization.service_id,
        plan_id: authorization.plan_id,
        authorization_hours: authorization.authorization_hours,
        complete: authorization.complete,
        auth_type: authorization.auth_type,
      };

      await auditApi.logEntry({
        action: "UPDATE_AUTHORIZATION_ATTEMPT",
        entity_type: "AUTHORIZATION",
        entity_id: formData.authorization_id,
        entity_identifier: `Auth #${formData.authorization_number}`,
        old_values: oldValues,
        new_values: formData,
        purpose_of_use: "OPERATIONS",
        contains_phi: false,
      });

      await secureAxios.put(`/api/auths/${formData.authorization_id}`, {
        ...formData,
        updated_by,
      });

      toast.success("Authorization updated successfully");
      onUpdated();
    } catch (err) {
      console.error("[X] Error updating authorization", err);
      toast.error("Failed to update authorization");

      await auditApi.logEntry({
        action: "UPDATE_AUTHORIZATION_ATTEMPT",
        entity_type: "AUTHORIZATION",
        entity_id: formData.authorization_id,
        action_status: "FAILED",
        action_reason: err.message,
        purpose_of_use: "OPERATIONS",
        contains_phi: false,
      });
    }
  };

  // ==========================================
  // Milestone Management Functions
  // ==========================================

  const handleAddMilestoneModeChange = (mode) => {
    setAddMilestoneMode(mode);
    setSelectedTemplateId("");
    setNewMilestones([]);

    if (mode === "auto") {
      const count = parseInt(formData.authorization_hours) || 1;
      setNewMilestones(
        Array.from({ length: count }, (_, i) => ({
          milestone_name: `Unit ${i + 1}`,
          description: "",
        }))
      );
    } else if (mode === "custom") {
      setNewMilestones([{ milestone_name: "Unit 1", description: "" }]);
    }
  };

  const handleTemplateSelect = (templateId) => {
    setSelectedTemplateId(templateId);
    if (!templateId) {
      setNewMilestones([]);
      return;
    }

    const template = milestoneTemplates.find((t) => t.template_id === templateId);
    if (template?.items) {
      setNewMilestones(
        template.items
          .sort((a, b) => a.sequence_order - b.sequence_order)
          .map((item) => ({
            milestone_name: item.milestone_name,
            description: item.description || "",
          }))
      );
    }
  };

  const updateNewMilestone = (index, field, value) => {
    const updated = [...newMilestones];
    updated[index] = { ...updated[index], [field]: value };
    setNewMilestones(updated);
  };

  const addNewMilestone = () => {
    setNewMilestones([
      ...newMilestones,
      { milestone_name: `Unit ${newMilestones.length + 1}`, description: "" },
    ]);
  };

  const removeNewMilestone = (index) => {
    if (newMilestones.length <= 1) return;
    setNewMilestones(newMilestones.filter((_, i) => i !== index));
  };

  const saveMilestones = async () => {
    if (newMilestones.length === 0) {
      toast.warning("Please add at least one milestone");
      return;
    }

    // HARD STOP: Block saving when milestone count doesn't match unit count
    if (
      parseInt(formData.authorization_hours) > 0 &&
      newMilestones.length !== parseInt(formData.authorization_hours)
    ) {
      toast.error(
        `Cannot save: You have ${newMilestones.length} milestone(s) but ${parseInt(formData.authorization_hours)} unit(s). Please adjust the milestones or the number of units to match.`
      );
      return;
    }

    setSavingMilestones(true);
    try {
      await secureAxios.post(
        `/api/auth-milestones/auth/${formData.authorization_id}`,
        {
          milestones: newMilestones.map((m, index) => ({
            milestone_name: m.milestone_name || `Unit ${index + 1}`,
            description: m.description || "",
            sequence_order: index + 1,
          })),
        }
      );

      toast.success("Milestones created successfully");
      setShowAddMilestones(false);
      setNewMilestones([]);

      // Refresh milestones
      const res = await secureAxios.get(
        `/api/auth-milestones/auth/${formData.authorization_id}`,
        { params: { tenant_id } }
      );
      setMilestones(res.data || []);
    } catch (err) {
      console.error("Error creating milestones:", err);
      toast.error(err.response?.data?.error || "Failed to create milestones");
    } finally {
      setSavingMilestones(false);
    }
  };

  const markMilestoneComplete = async (milestone) => {
    try {
      await secureAxios.post(
        `/api/auth-milestones/${milestone.milestone_id}/complete`
      );
      toast.success(`Milestone "${milestone.milestone_name}" marked as complete`);

      // Refresh milestones
      const res = await secureAxios.get(
        `/api/auth-milestones/auth/${formData.authorization_id}`,
        { params: { tenant_id } }
      );
      setMilestones(res.data || []);
    } catch (err) {
      console.error("Error completing milestone:", err);
      toast.error(err.response?.data?.error || "Failed to complete milestone");
    }
  };

  const reopenMilestone = async (milestone) => {
    try {
      await secureAxios.put(`/api/auth-milestones/${milestone.milestone_id}`, {
        is_complete: false,
      });
      toast.success(`Milestone "${milestone.milestone_name}" reopened`);

      // Refresh milestones
      const res = await secureAxios.get(
        `/api/auth-milestones/auth/${formData.authorization_id}`,
        { params: { tenant_id } }
      );
      setMilestones(res.data || []);
    } catch (err) {
      console.error("Error reopening milestone:", err);
      toast.error(err.response?.data?.error || "Failed to reopen milestone");
    }
  };

  const deleteMilestones = async () => {
    if (
      !window.confirm(
        "Are you sure you want to delete all milestones? This cannot be undone."
      )
    ) {
      return;
    }

    try {
      await secureAxios.delete(
        `/api/auth-milestones/auth/${formData.authorization_id}`
      );
      toast.success("Milestones deleted successfully");
      setMilestones([]);
    } catch (err) {
      console.error("Error deleting milestones:", err);
      toast.error(err.response?.data?.error || "Failed to delete milestones");
    }
  };

  // Start editing a milestone
  const startEditMilestone = (milestone) => {
    setEditingMilestoneId(milestone.milestone_id);
    setEditMilestoneData({
      milestone_name: milestone.milestone_name,
      description: milestone.description || "",
    });
  };

  // Cancel editing
  const cancelEditMilestone = () => {
    setEditingMilestoneId(null);
    setEditMilestoneData({ milestone_name: "", description: "" });
  };

  // Save edited milestone
  const saveEditMilestone = async (milestoneId) => {
    if (!editMilestoneData.milestone_name.trim()) {
      toast.warning("Milestone name is required");
      return;
    }

    try {
      await secureAxios.put(`/api/auth-milestones/${milestoneId}`, {
        milestone_name: editMilestoneData.milestone_name,
        description: editMilestoneData.description,
      });
      
      toast.success("Milestone updated successfully");
      setEditingMilestoneId(null);
      setEditMilestoneData({ milestone_name: "", description: "" });

      // Refresh milestones
      const res = await secureAxios.get(
        `/api/auth-milestones/auth/${formData.authorization_id}`,
        { params: { tenant_id } }
      );
      setMilestones(res.data || []);
    } catch (err) {
      console.error("Error updating milestone:", err);
      toast.error(err.response?.data?.error || "Failed to update milestone");
    }
  };

  // Delete a single milestone
  const deleteSingleMilestone = async (milestone) => {
    if (milestone.is_invoiced) {
      toast.error("Cannot delete an invoiced milestone");
      return;
    }

    if (parseInt(milestone.job_log_count) > 0) {
      toast.error("Cannot delete a milestone with job logs. Remove the job logs first.");
      return;
    }

    if (!window.confirm(`Are you sure you want to delete "${milestone.milestone_name}"?`)) {
      return;
    }

    try {
      await secureAxios.delete(`/api/auth-milestones/${milestone.milestone_id}`);
      toast.success("Milestone deleted");

      // Refresh milestones
      const res = await secureAxios.get(
        `/api/auth-milestones/auth/${formData.authorization_id}`,
        { params: { tenant_id } }
      );
      setMilestones(res.data || []);
    } catch (err) {
      console.error("Error deleting milestone:", err);
      toast.error(err.response?.data?.error || "Failed to delete milestone");
    }
  };

  // Add a single milestone
  const addSingleMilestone = async () => {
    const nextSequence = milestones.length + 1;
    const milestoneName = `Milestone ${nextSequence}`;

    try {
      await secureAxios.post(
        `/api/auth-milestones/auth/${formData.authorization_id}/add`,
        {
          milestone_name: milestoneName,
          description: "",
        }
      );
      toast.success("Milestone added");

      // Refresh milestones
      const res = await secureAxios.get(
        `/api/auth-milestones/auth/${formData.authorization_id}`,
        { params: { tenant_id } }
      );
      setMilestones(res.data || []);
    } catch (err) {
      console.error("Error adding milestone:", err);
      toast.error(err.response?.data?.error || "Failed to add milestone");
    }
  };

  // Check if any milestones are invoiced
  const hasInvoicedMilestones = milestones.some((m) => m.is_invoiced);
  const hasJobLogs = milestones.some((m) => parseInt(m.job_log_count) > 0);

  return (
    <div>
      {/* HIPAA Warning for Editing */}
      {showPrivacyNotice && (
        <div className="alert alert-warning alert-dismissible d-flex align-items-start mb-3">
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={() => setShowPrivacyNotice(false)}
          ></button>
          <i className="bi bi-shield-exclamation me-2 flex-shrink-0"></i>
          <div>
            <strong>Caution:</strong> You are editing an existing authorization
            that may contain PHI. Ensure all changes are accurate and necessary.
            This action will be logged with your user information and timestamp
            for audit purposes.
          </div>
        </div>
      )}

      {authWithoutPlan && (
        <div className="col-12 text-info mb-3">
          <i className="bi bi-info-circle me-1"></i>
          This authorization was created without a plan.
        </div>
      )}

      <form onSubmit={handleSubmit} className="row g-3">
        {/* Consumer */}
        <div className="col-md-4">
          <label className="form-label">Consumer</label>
          <select
            className="form-select"
            name="consumer_id"
            value={formData.consumer_id || ""}
            onChange={handleChange}
          >
            <option value="">Select Consumer</option>
            {uniqueConsumers.map((c) => (
              <option key={c.consumer_id} value={c.consumer_id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        {/* Counselor */}
        <div className="col-md-4">
          <label className="form-label">Counselor</label>
          <input
            type="text"
            className="form-control"
            value={
              consumers.find((c) => c.consumer_id === formData.consumer_id)
                ?.counselor_name || ""
            }
            disabled
          />
        </div>

        {/* Authorization Type */}
        <div className="col-md-4">
          <label className="form-label">Authorization Type</label>
          <select
            className="form-select"
            name="auth_type"
            value={formData.auth_type || "service"}
            onChange={handleChange}
          >
            <option value="service">Service</option>
            <option value="mileage">Mileage</option>
          </select>
        </div>

        {/* Plan */}
        {!authWithoutPlan && (
          <div className="col-md-4">
            <label className="form-label">Plan</label>
            <select
              className="form-select"
              name="plan_id"
              value={formData.plan_id || ""}
              onChange={handleChange}
            >
              <option value="">Select Plan</option>
              {[...new Map(plans.map((p) => [p.plan_id, p])).values()]
                .sort((a, b) =>
                  (a.plan_name || "").localeCompare(b.plan_name || "")
                )
                .map((p) => (
                  <option key={p.plan_id} value={p.plan_id}>
                    {p.plan_name || `Plan #${p.plan_id}`}
                  </option>
                ))}
            </select>
          </div>
        )}

        {/* Service */}
        <div className="col-md-4">
          <label className="form-label">Service</label>
          <select
            className="form-select"
            name="service_id"
            value={formData.service_id || ""}
            onChange={handleChange}
          >
            <option value="">Select Service</option>
            {services.map((s) => (
              <option key={s.service_id} value={s.service_id}>
                {s.service_name}
                {s.flat_rate && " (Flat Rate)"}
              </option>
            ))}
          </select>
        </div>

        {/* Authorization Number */}
        <div className="col-md-4">
          <label className="form-label">Authorization Number</label>
          <input
            className="form-control"
            name="authorization_number"
            value={formData.authorization_number || ""}
            onChange={handleChange}
          />
        </div>

        {/* Approval Date */}
        <div className="col-md-4">
          <label className="form-label">Approval Date</label>
          <input
            type="date"
            className="form-control"
            name="approval_date"
            value={
              formData.approval_date
                ? new Date(formData.approval_date).toISOString().split("T")[0]
                : ""
            }
            onChange={handleChange}
          />
        </div>

        {/* Approved By */}
        <div className="col-md-4">
          <label className="form-label">Approved By</label>
          <input
            type="text"
            className="form-control"
            name="approved_by"
            value={formData.approved_by || ""}
            onChange={handleChange}
          />
        </div>

        {/* Authorization Hours/Units */}
        <div className="col-md-4">
          <label className="form-label">
            {isFlatRateService ? "Number of Units" : "Authorization Hours/Miles"}
          </label>
          <input
            type="number"
            step={isFlatRateService ? "1" : "0.01"}
            className="form-control"
            name="authorization_hours"
            value={formData.authorization_hours || ""}
            onChange={handleChange}
          />
        </div>

        {/* Complete Toggle */}
        <div className="col-md-4 form-check align-self-end">
          <input
            className="form-check-input"
            type="checkbox"
            id="completeToggle"
            checked={!!formData.complete}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, complete: e.target.checked }))
            }
          />
          <label className="form-check-label ms-2" htmlFor="completeToggle">
            Mark as Complete
          </label>
          <small className="d-block text-muted">
            Check to exclude from mileage and job logs
          </small>
        </div>

        {/* No More Billing Toggle */}
        <div className="col-md-4 form-check align-self-end">
          <input
            className="form-check-input"
            type="checkbox"
            id="noMoreBillingToggle"
            checked={!!formData.no_more_billing}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                no_more_billing: e.target.checked,
              }))
            }
          />
          <label className="form-check-label ms-2" htmlFor="noMoreBillingToggle">
            No More Billing
          </label>
          <small className="d-block text-muted">
            Check to exclude from invoice generation
          </small>
        </div>

        {/* ==========================================
            MILESTONE SECTION - Only for flat rate
            ========================================== */}
        {isFlatRateService && (
          <div className="col-12">
            <div className="card border-info mt-3">
              <div className="card-header bg-info text-white d-flex justify-content-between align-items-center">
                <span>
                  <i className="bi bi-list-check me-2"></i>
                  Milestones ({milestones.length})
                </span>
                {milestones.length === 0 && !showAddMilestones && (
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    onClick={() => {
                      setShowAddMilestones(true);
                      handleAddMilestoneModeChange("auto");
                    }}
                  >
                    + Add Milestones
                  </button>
                )}
                {milestones.length > 0 && !hasInvoicedMilestones && !hasJobLogs && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    onClick={deleteMilestones}
                    title="Delete all milestones"
                  >
                    <i className="bi bi-trash"></i>
                  </button>
                )}
              </div>
              <div className="card-body">
                {loadingMilestones ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm" role="status"></div>
                    <span className="ms-2">Loading milestones...</span>
                  </div>
                ) : milestones.length === 0 && !showAddMilestones ? (
                  <div className="text-muted">
                    <i className="bi bi-info-circle me-2"></i>
                    No milestones defined. This authorization will be billed as a single unit.
                    <button
                      type="button"
                      className="btn btn-link p-0 ms-2"
                      onClick={() => {
                        setShowAddMilestones(true);
                        handleAddMilestoneModeChange("auto");
                      }}
                    >
                      Add milestones now
                    </button>
                  </div>
                ) : showAddMilestones ? (
                  /* Add Milestones Form */
                  <div>
                    <div className="mb-3">
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="addModeAuto"
                          checked={addMilestoneMode === "auto"}
                          onChange={() => handleAddMilestoneModeChange("auto")}
                        />
                        <label className="form-check-label" htmlFor="addModeAuto">
                          <strong>Auto-Generate</strong> - Create Unit 1, Unit 2, etc.
                        </label>
                      </div>
                      {milestoneTemplates.length > 0 && (
                        <div className="form-check mt-2">
                          <input
                            type="radio"
                            className="form-check-input"
                            id="addModeTemplate"
                            checked={addMilestoneMode === "template"}
                            onChange={() => handleAddMilestoneModeChange("template")}
                          />
                          <label className="form-check-label" htmlFor="addModeTemplate">
                            <strong>Use Template</strong> - Apply a predefined template
                          </label>
                        </div>
                      )}
                      <div className="form-check mt-2">
                        <input
                          type="radio"
                          className="form-check-input"
                          id="addModeCustom"
                          checked={addMilestoneMode === "custom"}
                          onChange={() => handleAddMilestoneModeChange("custom")}
                        />
                        <label className="form-check-label" htmlFor="addModeCustom">
                          <strong>Custom</strong> - Define your own milestones
                        </label>
                      </div>
                    </div>

                    {/* Template Selection */}
                    {addMilestoneMode === "template" && (
                      <div className="mb-3">
                        <select
                          className="form-select"
                          value={selectedTemplateId}
                          onChange={(e) => handleTemplateSelect(e.target.value)}
                        >
                          <option value="">-- Select Template --</option>
                          {milestoneTemplates.map((t) => (
                            <option key={t.template_id} value={t.template_id}>
                              {t.template_name} ({t.items?.length || 0} milestones)
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Milestone List */}
                    {newMilestones.length > 0 && (
                      <div className="mb-3">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>Milestones to Create:</strong>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-primary"
                            onClick={addNewMilestone}
                          >
                            + Add
                          </button>
                        </div>
                        {newMilestones.map((m, index) => (
                          <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                            <span className="badge bg-secondary">{index + 1}</span>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={m.milestone_name}
                              onChange={(e) =>
                                updateNewMilestone(index, "milestone_name", e.target.value)
                              }
                              placeholder="Milestone name"
                            />
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              value={m.description}
                              onChange={(e) =>
                                updateNewMilestone(index, "description", e.target.value)
                              }
                              placeholder="Description (optional)"
                            />
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => removeNewMilestone(index)}
                              disabled={newMilestones.length <= 1}
                            >
                              &times;
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Error: Count mismatch - must fix before saving */}
                    {newMilestones.length > 0 &&
                      parseInt(formData.authorization_hours) > 0 &&
                      newMilestones.length !== parseInt(formData.authorization_hours) && (
                        <div className="alert alert-danger py-2 mb-3">
                          <i className="bi bi-x-circle me-2"></i>
                          <strong>Cannot save:</strong> You have {newMilestones.length} milestone(s)
                          but {parseInt(formData.authorization_hours)} unit(s).
                          Please add/remove milestones or change the number of units to match.
                        </div>
                      )}

                    <div className="d-flex gap-2">
                      <button
                        type="button"
                        className="btn btn-success btn-sm"
                        onClick={saveMilestones}
                        disabled={
                          savingMilestones || 
                          newMilestones.length === 0 ||
                          (parseInt(formData.authorization_hours) > 0 &&
                           newMilestones.length !== parseInt(formData.authorization_hours))
                        }
                      >
                        {savingMilestones ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1"></span>
                            Saving...
                          </>
                        ) : (
                          "Save Milestones"
                        )}
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => {
                          setShowAddMilestones(false);
                          setNewMilestones([]);
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  /* Existing Milestones Table */
                  <div>
                    {/* Warning: Milestone count mismatch */}
                    {parseInt(formData.authorization_hours) > 0 &&
                      milestones.length !== parseInt(formData.authorization_hours) && (
                        <div className="alert alert-warning py-2 mb-3 d-flex justify-content-between align-items-center">
                          <div>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            <strong>Mismatch:</strong> You have {milestones.length} milestone(s) 
                            but {parseInt(formData.authorization_hours)} unit(s). 
                            {milestones.length > parseInt(formData.authorization_hours) 
                              ? " Delete extra milestones or increase units to match."
                              : " Add more milestones or reduce units to match."}
                          </div>
                          {/* Add Milestone button when we need more */}
                          {milestones.length < parseInt(formData.authorization_hours) && (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary ms-2"
                              onClick={addSingleMilestone}
                            >
                              <i className="bi bi-plus-circle me-1"></i>
                              Add Milestone
                            </button>
                          )}
                        </div>
                      )}

                    <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: "50px" }}>#</th>
                          <th>Milestone</th>
                          <th style={{ width: "100px" }}>Status</th>
                          <th style={{ width: "80px" }}>Logs</th>
                          <th style={{ width: "100px" }}>Hours</th>
                          <th style={{ width: "180px" }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {milestones
                          .sort((a, b) => a.sequence_order - b.sequence_order)
                          .map((m) => (
                            <tr key={m.milestone_id}>
                              <td className="text-center">{m.sequence_order}</td>
                              <td>
                                {editingMilestoneId === m.milestone_id ? (
                                  /* Edit Mode */
                                  <div>
                                    <input
                                      type="text"
                                      className="form-control form-control-sm mb-1"
                                      value={editMilestoneData.milestone_name}
                                      onChange={(e) =>
                                        setEditMilestoneData((prev) => ({
                                          ...prev,
                                          milestone_name: e.target.value,
                                        }))
                                      }
                                      placeholder="Milestone name"
                                      autoFocus
                                    />
                                    <input
                                      type="text"
                                      className="form-control form-control-sm"
                                      value={editMilestoneData.description}
                                      onChange={(e) =>
                                        setEditMilestoneData((prev) => ({
                                          ...prev,
                                          description: e.target.value,
                                        }))
                                      }
                                      placeholder="Description (optional)"
                                    />
                                  </div>
                                ) : (
                                  /* View Mode */
                                  <>
                                    <strong>{m.milestone_name}</strong>
                                    {m.description && (
                                      <small className="d-block text-muted">
                                        {m.description}
                                      </small>
                                    )}
                                  </>
                                )}
                              </td>
                              <td>
                                {m.is_invoiced ? (
                                  <span className="badge bg-success">
                                    <i className="bi bi-check-circle me-1"></i>
                                    Invoiced
                                  </span>
                                ) : m.is_complete ? (
                                  <span className="badge bg-primary">
                                    <i className="bi bi-check me-1"></i>
                                    Complete
                                  </span>
                                ) : (
                                  <span className="badge bg-secondary">
                                    In Progress
                                  </span>
                                )}
                              </td>
                              <td className="text-center">
                                {m.job_log_count || 0}
                              </td>
                              <td className="text-center">
                                {parseFloat(m.total_hours_logged || 0).toFixed(2)}
                              </td>
                              <td>
                                {editingMilestoneId === m.milestone_id ? (
                                  /* Edit Mode Actions */
                                  <div className="d-flex gap-1">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-success"
                                      onClick={() => saveEditMilestone(m.milestone_id)}
                                      title="Save"
                                    >
                                      <i className="bi bi-check"></i>
                                    </button>
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-secondary"
                                      onClick={cancelEditMilestone}
                                      title="Cancel"
                                    >
                                      <i className="bi bi-x"></i>
                                    </button>
                                  </div>
                                ) : m.is_invoiced ? (
                                  <span className="text-muted small">
                                    <i className="bi bi-lock me-1"></i>
                                    Locked
                                  </span>
                                ) : (
                                  /* View Mode Actions */
                                  <div className="d-flex gap-1">
                                    <button
                                      type="button"
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => startEditMilestone(m)}
                                      title="Edit milestone"
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    {m.is_complete ? (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => reopenMilestone(m)}
                                        title="Reopen"
                                      >
                                        Reopen
                                      </button>
                                    ) : (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-success"
                                        onClick={() => markMilestoneComplete(m)}
                                        disabled={parseInt(m.job_log_count) === 0}
                                        title={
                                          parseInt(m.job_log_count) === 0
                                            ? "Log time before marking complete"
                                            : "Mark as complete"
                                        }
                                      >
                                        Complete
                                      </button>
                                    )}
                                    {/* Delete button - only show if no job logs */}
                                    {parseInt(m.job_log_count) === 0 && (
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-outline-danger"
                                        onClick={() => deleteSingleMilestone(m)}
                                        title="Delete milestone"
                                      >
                                        <i className="bi bi-trash"></i>
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>

                    {/* Summary */}
                    <div className="mt-2 small text-muted">
                      <strong>Summary:</strong>{" "}
                      {milestones.filter((m) => m.is_invoiced).length} invoiced,{" "}
                      {milestones.filter((m) => m.is_complete && !m.is_invoiced).length} complete,{" "}
                      {milestones.filter((m) => !m.is_complete).length} in progress
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="col-12 d-flex justify-content-between mt-4">
          <button type="button" className="btn btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button className="btn btn-primary" type="submit">
            Save Changes
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditAuthorizationForm;